import { PipelineConfig, ReplacementConfig } from '../config-types';
import { FrameData, LadonOutput } from '../generator';
import { logger } from '../logger';
import { compileShader, linkProgram } from '../util';

import { LadonRenderer } from './ladon-renderer';
import { WebGLRenderer } from './webgl-renderer';

/**
 * WebGL renderer implementation for virtual background replacement effects.
 * Handles rendering of video backgrounds with proper frame rate throttling.
 */
export class ReplacementRenderer extends WebGLRenderer implements LadonRenderer {
    /** The compiled WebGL shader program used for rendering. */
    private program: WebGLProgram;

    /** Timestamp of the last video frame update in milliseconds. */
    private lastVideoFrameTime = 0;

    /** Interval between video frame updates in milliseconds. */
    private videoFrameInterval: number;

    /** WebGL texture containing the background image or video frame. */
    protected backgroundTexture!: WebGLTexture;

    /** Fragment shader source code for the replacement effect. */
    protected fragmentShader!: string;

    /**
     * Create a new replacement renderer instance.
     *
     * @param outputCanvas - The canvas element where the rendered output will be displayed.
     * @param config - Pipeline configuration containing render settings and background source.
     */
    constructor(public outputCanvas: HTMLCanvasElement, public readonly config: PipelineConfig) {
        super(outputCanvas, config);

        // Initialize video frame interval based on background source
        const renderConfig = this.config.render as ReplacementConfig;
        const backgroundSource = renderConfig.background;

        if (backgroundSource instanceof HTMLVideoElement) {
            // Use video's frame rate, or default to 30fps
            const videoFPS = this.getVideoFrameRate(backgroundSource) || 30;
            this.videoFrameInterval = 1000 / videoFPS;
        } else {
            this.videoFrameInterval = 0; // No throttling for static backgrounds
        }

        // Build the fragment shader
        this.fragmentShader = this.buildFragmentShader();

        // Compile and link shaders
        const vertexShader = compileShader(this.gl, this.gl.VERTEX_SHADER, this.vertex_shader_src);
        const fragmentShader = compileShader(this.gl, this.gl.FRAGMENT_SHADER, this.fragmentShader);

        this.program = linkProgram(this.gl, vertexShader, fragmentShader);
        this.gl.useProgram(this.program);

        this.setSharedUniforms(this.program);

        this.setupBackgroundTexture();
    }

    /**
     * Build the fragment shader source code for the replacement effect.
     * Creates a shader that blends the background with the input video based on the mask.
     *
     * @returns The complete fragment shader source code.
     */
    protected buildFragmentShader(): string {
        return `${this.fragment_shader_header}
    
uniform sampler2D u_background;

${this.shared_shader_functions}

void main() {
    ${this.getUVCalculation()}

    vec3 background = texture(u_background, st).rgb;
    vec3 image = texture(u_image, st).rgb;
    float mask = get_mask(st);

    vec3 color = mix(background, image, mask);
    outColor = vec4(color, 1.0);
}`;
    }

    /**
     * Set up the WebGL texture for the background image or video.
     * Creates and configures the texture that will be used as the replacement background.
     */
    protected setupBackgroundTexture(): void {
        const renderConfig = this.config.render as ReplacementConfig;
        const backgroundSource = renderConfig.background;

        this.backgroundTexture = this.createTexture(
            this.gl.RGBA,
            backgroundSource.width,
            backgroundSource.height,
            () => {
                this.gl.texImage2D(
                    this.gl.TEXTURE_2D,
                    0,
                    this.gl.RGBA,
                    this.gl.RGBA,
                    this.gl.UNSIGNED_BYTE,
                    backgroundSource
                );
            }
        );

        const uBackground = this.gl.getUniformLocation(this.program, 'u_background');
        if (!uBackground) {
            logger.errorAndThrow('Error finding "u_background" uniform');
        }
        this.gl.activeTexture(this.gl.TEXTURE2);
        this.gl.bindTexture(this.gl.TEXTURE_2D, this.backgroundTexture);
        this.gl.uniform1i(uBackground, 2);
    }

    /**
     * Render a frame with the replacement background effect.
     * Combines the input video stream with the background based on the provided mask.
     * Includes frame rate throttling for video backgrounds to prevent stuttering.
     * Canvas backgrounds are updated every frame to support dynamic content.
     *
     * @param input - The input frame data (video element or image data).
     * @param mask - Optional mask data for determining foreground/background blend.
     * @returns Promise that resolves when rendering is complete.
     */
    public async render(input: FrameData, mask?: LadonOutput): Promise<void> {
        this.uploadVideoStream(input);
        this.uploadMask(mask);

        const renderConfig = this.config.render as ReplacementConfig;
        const shouldDrawVideoBackground = !renderConfig.static;
        const backgroundSource = renderConfig.background;

        if (shouldDrawVideoBackground) {
            if (backgroundSource instanceof HTMLVideoElement) {
                // Throttle video backgrounds to prevent stuttering
                await this.updateVideoBackgroundTexture(backgroundSource);
            } else {
                // Always update canvas backgrounds (like dynamic Game of Life)
                this.gl.activeTexture(this.gl.TEXTURE2);
                this.gl.bindTexture(this.gl.TEXTURE_2D, this.backgroundTexture);

                this.gl.texImage2D(
                    this.gl.TEXTURE_2D,
                    0,
                    this.gl.RGBA,
                    this.gl.RGBA,
                    this.gl.UNSIGNED_BYTE,
                    backgroundSource
                );
            }
        }

        this.gl.useProgram(this.program);
        this.gl.drawArrays(this.gl.TRIANGLES, 0, this.verts.length / 3);
        this.postDraw();
    }

    /**
     * Throttle video background updates and upload texture only when needed.
     * This prevents stuttering and unnecessary GPU uploads when inference is skipped.
     *
     * @param backgroundSource - The video element to potentially upload.
     * @returns Promise that resolves after appropriate throttling and upload.
     */
    private async updateVideoBackgroundTexture(backgroundSource: HTMLVideoElement): Promise<void> {
        if (this.videoFrameInterval <= 0) return;

        const now = performance.now();
        const timeSinceLastUpdate = now - this.lastVideoFrameTime;

        if (timeSinceLastUpdate >= this.videoFrameInterval) {
            // Only upload texture when enough time has passed
            this.gl.activeTexture(this.gl.TEXTURE2);
            this.gl.bindTexture(this.gl.TEXTURE_2D, this.backgroundTexture);

            this.gl.texImage2D(
                this.gl.TEXTURE_2D,
                0,
                this.gl.RGBA,
                this.gl.RGBA,
                this.gl.UNSIGNED_BYTE,
                backgroundSource
            );

            this.lastVideoFrameTime = now;
        }
    }

    /**
     * Attempt to determine the frame rate of a video element.
     * Tries to extract frame rate from video track settings if available.
     *
     * @param video - The video element to analyze.
     * @returns The video's frame rate in frames per second, or undefined if not determinable.
     */
    private getVideoFrameRate(video: HTMLVideoElement): number | undefined {
        // Try to get frame rate from video tracks
        if (video.srcObject instanceof MediaStream) {
            const videoTrack = video.srcObject.getVideoTracks()[0];
            if (videoTrack?.getSettings) {
                return videoTrack.getSettings().frameRate;
            }
        }
        return undefined; // Fall back to default
    }

    /**
     * Load and initialize the renderer.
     * Currently a no-op for this renderer implementation.
     *
     * @returns Promise that resolves when loading is complete.
     */
    public async load(): Promise<void> {
        // empty
    }

    /**
     * Clean up resources and destroy the renderer.
     * Currently a no-op for this renderer implementation.
     */
    public destroy(): void {
        // empty
    }
}
